import { toast } from '@parsleyai/ui'
import { onAuthStateChanged } from 'firebase/auth'
import { createContext, type Dispatch, type ReactNode, useEffect, useReducer, useRef } from 'react'
import { auth } from '@/services/firebase'
import { purgeClientDataOnSignOut } from '@/services/storage/purgeClientData'
import { authReducer, initialAuthState } from './authReducer'
import { type AuthAction, AuthActionTypes, type AuthState } from './types'

interface AuthContextValue {
  state: AuthState
  dispatch: Dispatch<AuthAction>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState)
  // Tracks whether a user was signed in, so we purge browser-stored PII only on a real
  // sign-in → sign-out transition — not on the initial "no user" at boot (nothing to purge).
  const wasSignedInRef = useRef(false)

  useEffect(() => {
    dispatch({ type: AuthActionTypes.AUTH_INIT })

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (user) {
          console.log('User is signed in with UID:', user.uid)
          wasSignedInRef.current = true
          dispatch({ type: AuthActionTypes.AUTH_SUCCESS, payload: user })
        } else {
          console.log('User is signed out')
          // Covers every sign-out path (manual, post-account-deletion, forced token expiry),
          // since deleteUser and revocation both null the auth state through this listener.
          // The toast store is a module singleton the purge can't reach, and a live toast would
          // otherwise keep rendering over the login page — carrying the previous user's data.
          if (wasSignedInRef.current) {
            toast.dismiss()
            void purgeClientDataOnSignOut()
          }
          wasSignedInRef.current = false
          dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
        }
      },
      (error) => {
        console.error('Auth state change error:', error)
        dispatch({ type: AuthActionTypes.AUTH_ERROR, payload: error.message })
      }
    )

    return () => unsubscribe()
  }, [])

  return <AuthContext.Provider value={{ state, dispatch }}>{children}</AuthContext.Provider>
}
